import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Cloud, Bot, Zap, BarChart3, ArrowRight } from 'lucide-react';
import { fadeInUp, staggerContainer } from '../../utils/animations';

const modules = [
  {
    id: 'cloud-infra',
    title: 'Cloud Infrastructure',
    description: 'Scalable cloud solutions with AWS, Azure, and GCP',
    icon: Cloud,
    image: '/bg1.avif',
    link: '/cloud-infra',
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'ai-agents',
    title: 'AI Agents',
    description: 'Intelligent automation with advanced AI capabilities',
    icon: Bot,
    image: '/bg2.avif',
    link: '/ai-agents',
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'automations',
    title: 'Automations',
    description: 'Streamline workflows with powerful automation tools',
    icon: Zap,
    image: '/bbright.avif',
    link: '/automations',
    color: 'from-yellow-500 to-orange-500'
  },
  {
    id: 'analytics',
    title: 'Analytics',
    description: 'Data-driven insights for better decision making',
    icon: BarChart3,
    image: '/tools.avif',
    link: '/analytics',
    color: 'from-green-500 to-emerald-500'
  }
];

const ModulesShowcase = () => {
  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-dark-surface">
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold mb-4">
            We Ship <span className="gradient-text">All the Time</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-text-secondary max-w-2xl mx-auto">
            Explore our cutting-edge modules designed to accelerate your business
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 isolate"
        >
          {modules.map((module) => {
            const Icon = module.icon;
            return (
              <motion.div key={module.id} variants={fadeInUp}>
                <Link to={module.link} className="block group">
                  <div className="relative overflow-hidden rounded-2xl glass-morphism hover:shadow-glow-purple transition-all duration-300">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={module.image}
                        alt={module.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-dark-bg via-dark-bg/50 to-transparent" />
                      <div className={`absolute top-4 right-4 w-12 h-12 rounded-xl bg-gradient-to-br ${module.color} flex items-center justify-center`}>
                        <Icon size={24} className="text-white" />
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-2 group-hover:text-neon-purple transition-colors">
                        {module.title}
                      </h3>
                      <p className="text-text-secondary text-sm mb-4">{module.description}</p>
                      <div className="flex items-center text-neon-purple text-sm font-medium">
                        Learn More
                        <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ModulesShowcase;

